import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { tpoGenUrls } from 'config/urls';
import { TPOGEN_PLAN_CREATED, TPOGEN_PLAN_CREATE_FAILED } from 'utils/mixpanelEventNames';
import { tpoGenKeys } from '../utils/tpoGenQueryKeys';
import { sendNotification } from 'utils/helperMethods';
import { trackTpoGenEvent, TPOGEN_PAGE_NAMES } from '../utils/trackTpoGenEvent';

export interface CreatePlanPayload {
    plan_name: string;
    dim1_attrs: string[];
    dim2_attrs: string[];
    plan_year: number;
    plan_version: string;
    baseline_vol_version?: string;
    /** Analytics-only: which UI entry point created this plan (see useCreatePlanMutation onSuccess). */
    creation_method?: 'modal' | 'planning_group';
    [key: string]: unknown;
}

export interface CreatePlanResponse {
    plan_id: string;
    planning_group_id: string;
    plan_creation_status: string;
    error_message: string;
}

const createPlan = async (payload: CreatePlanPayload) => {
    const token = localStorage.getItem('accessToken');
    const response = await axios(tpoGenUrls.createPlan, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
        },
        data: {
            ...payload,
            created_by: localStorage.getItem('email'),
        },
    });

    if (response?.data?.plan_creation_status === 'failed') {
        throw new Error(response?.data?.error_message);
    }

    return response.data as CreatePlanResponse;
};

const useCreatePlanMutation = ({
    onSuccess,
}: {
    onSuccess?: (data: CreatePlanResponse, payload: CreatePlanPayload) => void;
}) => {
    const queryClient = useQueryClient();

    const { mutate, isPending } = useMutation({
        mutationFn: (payload: CreatePlanPayload) => createPlan(payload),
        onSuccess: (data, payload) => {
            queryClient.invalidateQueries({ queryKey: tpoGenKeys.planningGroups() });
            queryClient.removeQueries({ queryKey: [...tpoGenKeys.all, 'plan-list'] });
            queryClient.invalidateQueries({ queryKey: [...tpoGenKeys.all, 'importable-plans'] });
            sendNotification('success', 'Plan created successfully');
            trackTpoGenEvent(TPOGEN_PLAN_CREATED, TPOGEN_PAGE_NAMES.PLANS_LIST, {
                // 'modal' = top-level "Create Plan" CTA (blank plan); 'planning_group' =
                // "Create New Plan" row inside an existing planning group. CSV's
                // blank|copy enum doesn't fit either case — confirm the intended values
                // with PM (see note in CreatePlanModal.tsx handleFinish).
                creation_method: payload.creation_method ?? 'modal',
                plan_id: data.plan_id,
                baseline_version_id: payload.baseline_vol_version,
            });
            onSuccess?.(data, payload);
        },
        onError: (error: any) => {
            const message = error?.response?.data?.detail || error?.response?.data?.message || error?.message || 'Failed to create plan';
            sendNotification('error', message);
            trackTpoGenEvent(TPOGEN_PLAN_CREATE_FAILED, TPOGEN_PAGE_NAMES.PLANS_LIST, { failure_reason: message });
        },
    });

    return {
        mutate,
        isPending,
    };
};

export default useCreatePlanMutation;
